import { useEffect, useState } from 'react';
import type { IPortfolioCodeQualitySummary } from '@skystone/shared';
import { ErrorBanner, Spinner } from '@/components/ui';
import { portfolioCodeQualityApi } from '@/services/portfolio-code-quality-api';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { CaveatBannerSlot } from './CaveatBannerSlot';
import { DistributionColumnSlot } from './DistributionColumnSlot';
import { FreshnessFooterSlot } from './FreshnessFooterSlot';
import { NoSonarOnboardedState } from './NoSonarOnboardedState';
import { TeamBreakoutsSlot } from './TeamBreakoutsSlot';
import { TrendColumnSlot } from './TrendColumnSlot';
import { VerdictStripSlot } from './VerdictStripSlot';

interface CodeQualityShellProps {
  filters: IMetricViewFilters;
}

/**
 * Code Quality tab shell. Owns the page-level fetch of `code-quality-summary`,
 * which feeds the caveat banner, verdict strip, freshness footer, and
 * page-level empty-state decision in a single round-trip.
 *
 * US3-US7 fill the inner slot components; the shell layout (responsive grid
 * wrappers, composition order) is frozen by US2 and not modified downstream.
 *
 * Layout rationale (per epic.md per-card degradation matrix, D18):
 *   base/sm/md → 1-col stack (trend column above distribution column)
 *   lg+        → 2/3 + 1/3 split (trend left, distribution right)
 * Team breakouts auto-hide when filters.teamId is set.
 */
export function CodeQualityShell({ filters }: Readonly<CodeQualityShellProps>) {
  const [summary, setSummary] = useState<IPortfolioCodeQualitySummary | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSummary(undefined);
    setError(null);

    portfolioCodeQualityApi
      .getSummary({
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
        teamId: filters.teamId || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setSummary(response);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load Code Quality data. Please try again.');
        setSummary(null);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.to, filters.productId, filters.teamId]);

  if (summary === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-3 p-2">
      {error && <ErrorBanner message={error} />}
      <CaveatBannerSlot counters={summary?.caveatCounters ?? null} />

      {summary?.inScopeRepos === 0 ? (
        <NoSonarOnboardedState />
      ) : (
        <>
          <VerdictStripSlot summary={summary} />
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <TrendColumnSlot filters={filters} />
            </div>
            <div>
              <DistributionColumnSlot filters={filters} inScopeRepos={summary?.inScopeRepos ?? 0} />
            </div>
          </div>
          {!filters.teamId && <TeamBreakoutsSlot filters={filters} />}
        </>
      )}

      <FreshnessFooterSlot freshness={summary?.freshness ?? null} />
    </div>
  );
}
